import { Fragment, useState, useEffect, useMemo, createContext, useContext } from 'react';
import { api } from '../lib/api.js';
import { useMyFlags, flagOn } from '../lib/flags.js';
import { pickDriveImage } from '../lib/driveImage.js';
import { journeyFlow, journeySpanHours } from '../lib/journeyFlow.mjs'; // the plan behind the tree (pure, unit-tested)
import HtmlStepPreview from './HtmlStepPreview.jsx';
import JourneySplitEditor from './JourneySplitEditor.jsx';
import { GATES, waitMismatch, sayHours, wordingPatchFromWait, waitPatchFromWording, PALETTE } from '../lib/journeyEdit.mjs'; // pure tree surgery + the wording-vs-wait check and its two one-tap fixes (unit-tested in test/journeyEdit, test/journeyMove)
import Icon from './Icon.jsx';
import VideoField from './VideoField.jsx';

// The journey DECISION TREE renderer — shared by the Owl chat's draftJourney
// confirm card and the Engage → Journeys tab's recipe previews. A journey is a
// tree of `message` + `decision` nodes (see server/journeys.js); decisions fan
// out into side-by-side branch columns with connector lines, and the whole tree
// scrolls horizontally when wider than its container (mobile-safe).

const fmtDelay = (h) => {
  if (!h) return 'right away';
  if (h % 24 === 0) { const d = h / 24; return `after ${d} day${d === 1 ? '' : 's'}`; }
  if (h < 24) return `after ${h}h`;
  return `after ${Math.round((h / 24) * 10) / 10} days`;
};
const fmtWait = (h) => {
  if (!h) return '';
  if (h % 24 === 0) { const d = h / 24; return `waits up to ${d} day${d === 1 ? '' : 's'}`; }
  return `waits up to ${h}h`;
};

// When the journey has a START moment (a scheduled start, or the date it went
// live), every step's relative delay becomes an actual date — which is the thing
// you actually want to know: "when does THIS email go?". Held in context rather
// than drilled through four components for one value.
const ScheduleCtx = createContext(null); // { start: epoch ms, byId: Map(nodeId → flow row) }
const fmtWhen = (ms) => new Date(ms).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
// The date a step goes. journeyFlow already ran the clock from the journey's start
// through every delay above this node, so the instant is read off the row — NOT
// rebuilt from `row.after`, which is only the gap since the step before it (doing
// that showed every step past the first in a branch early by the whole chain above).
function useGoesAt(node) {
  const sched = useContext(ScheduleCtx);
  if (node.sendAt) { const t = Date.parse(node.sendAt); return Number.isFinite(t) ? { at: t, pinned: true } : null; }
  if (!sched) return null;
  const row = sched.byId.get(node.id);
  if (!row || !Number.isFinite(row.at)) return null;
  // A branch that fires on an ACTION (clicked/opened/bought) can happen any time
  // from here — the computed moment is the earliest, not a promise. Say so.
  // A HELD step has no date at all: it sends to nobody until the branch resumes,
  // and it is then re-timed from that moment. Showing the date it WOULD have gone
  // reads as "this went out", which is how a paused arm got mistaken for a send.
  return { at: row.at, trigger: row.trigger || '', paused: !!row.paused };
}

// ─── The builder: drop slots and moving ───────────────────────────────────────
// Held in context rather than drilled through DecisionSplit → NodeColumn → card,
// which is four signatures deep for something only two leaf components use.
//
// ONE affordance does both jobs. Every gap between two nodes (and the gap at the
// front, and the one at the end) is a SLOT. Idle, it offers ＋ — the palette,
// dropped in right here, which is the thing the old editor could never do: it
// only ever appended to the end of a branch. With a card picked up, the same slot
// becomes "move here".
//
// PICKING UP IS A TAP, not only a drag. `armed` is the node currently lifted, set
// by the ⠿ handle — so the whole builder works on a phone, where HTML5 drag does
// not exist. Mouse users additionally get real dragging, which sets the same
// state. (Same pattern as EmailBuilder's blocks: drag on desktop, buttons on touch.)
const BuilderCtx = createContext(null); // { armed, setArmed, onMove, onInsert, refuse, fits, atCap }

const BRANCH_COLORS = ['#7c3aed', '#0ea5e9', '#f59e0b', '#ec4899', '#10b981'];

const TREE_CSS = `
.jt-scroll { overflow-x: auto; padding: 4px 2px 10px; }
.jt-col { display: inline-flex; flex-direction: column; align-items: center; }
.jt-link { width: 2px; height: 18px; background: var(--hairline); flex: none; }
.jt-stem { width: 2px; height: 16px; background: var(--hairline); }
.jt-branches { display: flex; align-items: flex-start; justify-content: center; }
.jt-branch { position: relative; padding: 18px 9px 0; display: flex; flex-direction: column; align-items: center; }
.jt-branch::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: var(--hairline); }
.jt-branch:first-child::before { left: 50%; }
.jt-branch:last-child::before { right: 50%; }
.jt-branch:only-child::before { display: none; }
.jt-branch::after { content: ''; position: absolute; top: 0; left: calc(50% - 1px); width: 2px; height: 18px; background: var(--hairline); }
.jt-card { width: 210px; box-sizing: border-box; }
`;

function channelChip(channel) {
  if (channel === 'whatsapp') return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', borderRadius: 980, padding: '2px 8px', background: 'rgba(37,211,102,0.14)', color: '#128c4b' }}>WhatsApp</span>;
  const email = channel === 'email';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', borderRadius: 980, padding: '2px 8px', background: email ? 'rgba(124,58,237,0.12)' : 'rgba(16,185,129,0.14)', color: email ? 'var(--brand)' : '#0d9668' }}>{email ? '✉️ Email' : '💬 SMS'}</span>
  );
}
function statChips(node, stats) {
  if (!stats) return null;
  const s = (node.step != null && stats.byStep && stats.byStep[node.step]) || {};
  const waiting = (node.id && stats.atNode && stats.atNode[node.id]) || 0;
  if (!s.opened && !s.clicked && !waiting) return null;
  const chip = { fontSize: 11, fontWeight: 700, borderRadius: 980, padding: '2px 8px', background: 'rgba(128,128,128,0.10)', color: 'var(--muted)' };
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 7 }}>
      {s.opened ? <span style={chip}>👁 {s.opened} opened</span> : null}
      {s.clicked ? <span style={{ ...chip, color: 'var(--brand)', background: 'rgba(var(--brand-rgb,255,56,92),0.10)' }}>🖱 {s.clicked} clicked</span> : null}
      {waiting ? <span style={{ ...chip, color: '#b45309', background: 'rgba(245,158,11,0.12)' }}>⏳ {waiting} here now</span> : null}
    </div>
  );
}
// An ISO instant ⇄ the value a <input type="datetime-local"> wants, in the
// reader's own timezone (the control has no zone, so converting via UTC would
// silently shift the send by the offset).
const toLocalInput = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const fromLocalInput = (v) => { const t = Date.parse(v); return Number.isFinite(t) ? new Date(t).toISOString() : ''; };

// WHEN this step goes: either a delay relative to the step before it, or an
// absolute date/time. `sendAt` wins over `delayHours` in the engine (see dueAt
// in server/journeys.js), so the editor offers them as one either/or control
// rather than two fields that can quietly disagree.
function DelayEditor({ node, onEdit }) {
  const onDate = !!node.sendAt;
  const days = node.delayHours > 0 && node.delayHours % 24 === 0;
  const val = days ? node.delayHours / 24 : node.delayHours || 0;
  const unitH = days ? 24 : 1;
  const inp = { border: '1px solid var(--hairline)', borderRadius: 7, background: 'var(--bg)', color: 'var(--text)', padding: '2px 6px', fontSize: 11.5, fontFamily: 'inherit' };
  const past = onDate && Date.parse(node.sendAt) < Date.now();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: 'var(--muted)', fontWeight: 600, flexWrap: 'wrap' }}>
      <select
        value={onDate ? 'on' : 'after'}
        style={inp}
        title="Send a set time after the previous step, or on a specific date"
        onChange={(e) => (e.target.value === 'on'
          // Default to this time tomorrow — a date in the past would send at once.
          ? onEdit(node.id, { sendAt: new Date(Date.now() + 86400e3).toISOString() })
          : onEdit(node.id, { sendAt: '' }))}
      >
        <option value="after">send after</option>
        <option value="on">send on</option>
      </select>
      {onDate ? (
        <>
          <input type="datetime-local" value={toLocalInput(node.sendAt)} style={{ ...inp, width: 172 }}
            onChange={(e) => onEdit(node.id, { sendAt: fromLocalInput(e.target.value) })} />
          {past && <span style={{ fontWeight: 700, color: '#b45309' }}>(in the past — sends as soon as they reach it)</span>}
        </>
      ) : (
        <>
          <input type="number" min="0" max={days ? 365 : 8760} value={val} style={{ ...inp, width: 46 }}
            onChange={(e) => onEdit(node.id, { delayHours: Math.max(0, Math.min(8760, (Number(e.target.value) || 0) * unitH)) })} />
          <select value={days ? 'd' : 'h'} style={inp}
            onChange={(e) => onEdit(node.id, { delayHours: e.target.value === 'd' ? (val || 1) * 24 : val })}>
            <option value="h">hours</option>
            <option value="d">days</option>
          </select>
          {!node.delayHours && <span style={{ fontWeight: 700 }}>(right away)</span>}
        </>
      )}
    </span>
  );
}
// How long a BEHAVIOUR decision holds the window open before the silent are sent
// down the timeout branch. Splits fork instantly on who someone is, so they never
// wait and never show this. The server clamps to 1 hour … 30 days, so the control
// does too rather than letting a save quietly rewrite what you typed.
function WaitEditor({ node, onEdit }) {
  const days = node.waitHours > 0 && node.waitHours % 24 === 0;
  const val = days ? node.waitHours / 24 : node.waitHours || 0;
  const inp = { border: '1px solid var(--hairline)', borderRadius: 7, background: 'var(--bg)', color: 'var(--text)', padding: '2px 6px', fontSize: 11.5, fontFamily: 'inherit' };
  const clamp = (h) => Math.max(1, Math.min(720, Math.round(h) || 1));
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: 'var(--muted)', fontWeight: 600, whiteSpace: 'nowrap' }}>
      waits up to
      <input type="number" min="1" max={days ? 30 : 720} value={val} style={{ ...inp, width: 46 }}
        title="How long to wait for them to act before the no-response branch fires"
        onChange={(e) => onEdit(node.id, { waitHours: clamp((Number(e.target.value) || 1) * (days ? 24 : 1)) })} />
      <select value={days ? 'd' : 'h'} style={inp}
        onChange={(e) => onEdit(node.id, { waitHours: clamp(e.target.value === 'd' ? (val || 1) * 24 : val) })}>
        <option value="h">hours</option>
        <option value="d">days</option>
      </select>
    </span>
  );
}
// "When does THIS email actually go?" — shown once the journey has a start moment.
// A pinned date is exact; a step reached by a delay chain is exact too; a step on
// an action branch is the EARLIEST it could go, so it is marked as such rather
// than presented as a promise.
function GoesAt({ node }) {
  const g = useGoesAt(node);
  if (!g) return null;
  if (g.paused) return (
    <span
      title="This step is on hold — it sends to nobody, and anyone already on this branch waits here. Resume it and the timing starts again from then."
      style={{ fontSize: 11, fontWeight: 700, color: '#b45309', background: 'rgba(245,158,11,0.12)', borderRadius: 980, padding: '1px 8px', whiteSpace: 'nowrap' }}
    >
      on hold — no send date
    </span>
  );
  const exact = g.pinned || !g.trigger;
  return (
    <span
      title={g.pinned ? 'This step is pinned to a date' : g.trigger ? `Goes ${g.trigger} — this is the earliest it could` : 'Worked out from the start date plus every delay before it'}
      style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', background: 'rgba(14,165,233,0.10)', borderRadius: 980, padding: '1px 8px', whiteSpace: 'nowrap' }}
    >
      {exact ? '' : 'from '}{fmtWhen(g.at)}{g.pinned ? ' · pinned' : ''}
    </span>
  );
}
// The little ✕ that removes a step (edit mode only) — confirmation + guards live
// in the owner's onRemove handler, this is just the affordance.
function RemoveBtn({ onClick, title }) {
  return <button type="button" onClick={onClick} title={title} style={{ marginLeft: 'auto', border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'var(--muted)', borderRadius: 7, padding: '1px 7px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', flex: 'none' }}>✕</button>;
}
function MessageCard({ node, stats, onEdit, onRemove, onGate, templates, entityId = '' }) {
  const myFlags = useMyFlags(entityId || null);
  const bctx = useContext(BuilderCtx);
  const isWa = node.channel === 'whatsapp';
  const waTpls = (bctx && bctx.waTemplates) || [];
  const waTpl = isWa ? waTpls.find((t) => t.id === node.waTemplateId) || null : null;
  const editIn = { width: '100%', boxSizing: 'border-box', border: '1px solid var(--hairline)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', padding: '5px 8px', fontSize: 12.5, fontFamily: 'inherit' };
  const pickImage = async (e) => {
    const f = e.target.files && e.target.files[0]; if (e.target) e.target.value = '';
    if (!f) return;
    const r = new FileReader();
    r.onload = () => onEdit(node.id, { heroImage: String(r.result || '').slice(0, 1500000) });
    r.readAsDataURL(f);
  };
  // Same artwork choices as the campaign builder's ImageField — a journey step
  // shouldn't be the one place you can only upload from your own machine.
  const [driveBusy, setDriveBusy] = useState(false);
  const driveOn = !!entityId && flagOn(myFlags, 'integrations.drive');
  const fromDrive = async () => {
    setDriveBusy(true);
    try { const url = await pickDriveImage(entityId); if (url) onEdit(node.id, { heroImage: url }); }
    catch (e) { window.alert(e.message); }
    setDriveBusy(false);
  };
  const applyTemplate = (id) => {
    const t = (templates || []).find((x) => String(x.id) === String(id));
    if (!t) return;
    if ((t.contentMode || 'template') === 'html') {
      onEdit(node.id, { contentMode: 'html', customHtml: t.customHtml || '', ...(t.subject ? { subject: t.subject } : {}) });
    } else {
      onEdit(node.id, {
        contentMode: 'template',
        ...(t.subject ? { subject: t.subject } : {}),
        ...(t.body ? { body: t.body } : {}),
        ...(t.ctaText ? { ctaText: t.ctaText } : {}),
        ...(t.heroImage ? { heroImage: t.heroImage } : {}),
      });
    }
  };
  // Built-template + custom-HTML templates apply per mailer; block-designer ones
  // stay whole-campaign (a node isn't a block canvas).
  const usableTemplates = (templates || []).filter((t) => (t.contentMode || 'template') !== 'blocks');
  const pickHtmlFile = async (e) => {
    const f = e.target.files && e.target.files[0]; if (e.target) e.target.value = '';
    if (!f) return;
    try { const t = await f.text(); onEdit(node.id, { contentMode: 'html', customHtml: t.slice(0, 300000) }); } catch { /* ignore */ }
  };
  const isHtml = node.contentMode === 'html';
  return (
    <div style={{ padding: 13, border: '1px solid var(--hairline)', borderRadius: 12, background: 'var(--card)', width: '100%', boxSizing: 'border-box', textAlign: 'left' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        {channelChip(node.channel)}
        {onEdit ? <DelayEditor node={node} onEdit={onEdit} /> : <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)' }}>{node.sendAt ? `on ${new Date(node.sendAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : fmtDelay(node.delayHours)}</span>}
        {node.paused && <span style={{ fontSize: 10.5, fontWeight: 800, color: '#b45309', background: 'rgba(245,158,11,0.14)', borderRadius: 980, padding: '1px 8px' }}>on hold</span>}
        <GoesAt node={node} />
        {/* Hold ONE step: it sends to nobody and people wait here until it resumes
            — the surgical version of pausing the whole campaign. */}
        {onEdit && <button type="button" onClick={() => onEdit(node.id, { paused: !node.paused })}
          title={node.paused ? 'Resume this step — anyone waiting here is picked up from this point, nothing is re-sent' : 'Hold this step — it sends to nobody and people wait here until you resume it'}
          style={{ border: '1px solid var(--hairline)', background: 'var(--bg)', color: node.paused ? '#b45309' : 'var(--muted)', borderRadius: 7, padding: '1px 8px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>{node.paused ? 'Resume' : 'Hold'}</button>}
        {onEdit && <MoveHandle id={node.id} />}
        {onEdit && onRemove && <RemoveBtn onClick={() => onRemove(node.id)} title="Remove this step" />}
      </div>
      {onEdit && isWa ? (
        // A WhatsApp step: the approved template IS the content (wording fixed at
        // approval) — pick it, add its header picture/clip if it has one, write the
        // reply to a tap, and the link the placeholders + buttons resolve to.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {waTpls.length ? (
            <select style={{ ...editIn, fontWeight: 600 }} value={node.waTemplateId || ''} onChange={(e) => onEdit(node.id, { waTemplateId: e.target.value })}>
              <option value="">Pick the approved template…</option>
              {waTpls.map((t) => <option key={t.id} value={t.id}>{t.name || '(unnamed)'} · {t.language}{t.header === 'image' ? ' · image' : t.header === 'video' ? ' · video' : ''}</option>)}
            </select>
          ) : <div style={{ fontSize: 12, color: 'var(--muted)' }}>No approved WhatsApp templates yet — request one in the campaign's WhatsApp section.</div>}
          {waTpl && <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap', background: 'rgba(37,211,102,0.08)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: 8, padding: '7px 9px' }}>{waTpl.body || '(no preview copy saved)'}</div>}
          {waTpl && ((waTpl.buttons || []).length > 0 || (waTpl.urlButtons || []).length > 0) && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {(waTpl.buttons || []).map((b) => <span key={b} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 999, border: '1px solid rgba(37,211,102,0.5)', color: '#128c7e', fontWeight: 600 }}>{b}</span>)}
              {(waTpl.urlButtons || []).map((b) => <span key={`u-${b.text}`} style={{ fontSize: 11, padding: '3px 9px', borderRadius: 999, border: '1px solid rgba(37,99,235,0.5)', color: '#2563eb', fontWeight: 600 }}>↗ {b.text}</span>)}
            </div>
          )}
          {waTpl?.header === 'image' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {node.heroImage
                ? <><img src={node.heroImage} alt="" style={{ height: 34, borderRadius: 6, maxWidth: 90, objectFit: 'cover' }} /><button type="button" onClick={() => onEdit(node.id, { heroImage: '' })} style={{ border: '1px solid var(--hairline)', background: 'none', borderRadius: 7, padding: '3px 8px', fontSize: 11, color: 'var(--muted)', cursor: 'pointer' }}>Remove picture</button></>
                : <>
                  <label style={{ border: '1px dashed var(--hairline)', borderRadius: 7, padding: '4px 10px', fontSize: 11.5, color: 'var(--muted)', cursor: 'pointer' }}><Icon name="image" size={12} /> Header image (required)<input type="file" accept="image/*" onChange={pickImage} style={{ display: 'none' }} /></label>
                  {driveOn && <button type="button" disabled={driveBusy} onClick={fromDrive} style={{ border: '1px dashed var(--hairline)', background: 'none', borderRadius: 7, padding: '4px 10px', fontSize: 11.5, color: 'var(--muted)', cursor: 'pointer' }}>Drive</button>}
                </>}
            </div>
          )}
          {waTpl?.header === 'video' && <VideoField value={node.heroImage || ''} onChange={(v) => onEdit(node.id, { heroImage: v })} entityId={entityId} compact />}
          {waTpl && (waTpl.buttons || []).length > 0 && (
            <textarea style={{ ...editIn, resize: 'vertical', lineHeight: 1.45 }} rows={3} value={node.waFollowUp || ''} placeholder={`Your reply when someone taps ${waTpl.buttons[0]} — your own words, no approval needed ({{name}}, {{link}} fill in; blank = just the link)`} onChange={(e) => onEdit(node.id, { waFollowUp: e.target.value.slice(0, 1000) })} />
          )}
          {waTpl && (waTpl.buttons || []).length > 0 && (
            <textarea style={{ ...editIn, resize: 'vertical', lineHeight: 1.45 }} rows={2} value={Array.isArray(node.waOpeners) ? node.waOpeners.join('\n') : (node.waOpeners || '')} placeholder="Opening suggestions under that reply — this step's own, up to 3, one per line, 20 characters each (blank = the client-level ones from Fan Owl → WhatsApp)" onChange={(e) => onEdit(node.id, { waOpeners: e.target.value.split('\n').map((x) => x.slice(0, 20)).slice(0, 3) })} />
          )}
          <input style={{ ...editIn }} value={node.ctaUrl || ''} placeholder="Link (blank = campaign buy link) — fills {{link}}, button taps and link buttons" onChange={(e) => onEdit(node.id, { ctaUrl: e.target.value })} />
          {waTpl && !waTpl.params?.includes('cta_url') && !(waTpl.urlButtons || []).length && !(waTpl.buttons || []).length && <div style={{ fontSize: 11, color: 'var(--muted)' }}>This template has no link placeholder or button, so the link is not used.</div>}
        </div>
      ) : onEdit ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {node.channel === 'email' && (
            <div style={{ display: 'flex', gap: 4 }}>
              {[['template', 'Built template'], ['html', 'Custom HTML']].map(([k, lbl]) => (
                <button key={k} type="button" onClick={() => onEdit(node.id, { contentMode: k })} style={{ flex: 1, border: '1px solid var(--hairline)', borderRadius: 7, padding: '3px 6px', fontSize: 11, fontWeight: 700, cursor: 'pointer', background: (node.contentMode || 'template') === k ? 'var(--brand)' : 'var(--bg)', color: (node.contentMode || 'template') === k ? '#fff' : 'var(--muted)' }}>{lbl}</button>
              ))}
            </div>
          )}
          {node.channel === 'email' && usableTemplates.length > 0 && (
            <select style={{ ...editIn, color: 'var(--muted)' }} value="" onChange={(e) => applyTemplate(e.target.value)}>
              <option value="">📝 Apply a template…</option>
              {usableTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}{(t.contentMode || 'template') === 'html' ? ' (HTML)' : ''}</option>)}
            </select>
          )}
          {node.channel === 'email' && <input style={{ ...editIn, fontWeight: 700 }} value={node.subject} placeholder="Subject" onChange={(e) => onEdit(node.id, { subject: e.target.value })} />}
          {/* Inbox preview text for THIS mailer — blank inherits the campaign's. */}
          {node.channel === 'email' && <input style={editIn} value={node.preheader || ''} maxLength={200} placeholder="Preview text (the line after the subject)" onChange={(e) => onEdit(node.id, { preheader: e.target.value })} />}
          {node.channel === 'email' && isHtml ? (
            // The rendered email leads, the markup is one tap away. A wall of raw
            // HTML told you nothing about whether the step had the right mail in it.
            <HtmlStepPreview node={node} entityId={entityId} onEdit={onEdit} onUpload={pickHtmlFile} />
          ) : (
            <>
              {node.channel === 'email' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {node.heroImage
                    ? <><img src={node.heroImage} alt="" style={{ height: 34, borderRadius: 6, maxWidth: 90, objectFit: 'cover' }} /><button type="button" onClick={() => onEdit(node.id, { heroImage: '' })} style={{ border: '1px solid var(--hairline)', background: 'none', color: 'var(--muted)', borderRadius: 7, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}>✕ remove</button></>
                    : <>
                      <label style={{ border: '1px dashed var(--hairline)', borderRadius: 7, padding: '4px 10px', fontSize: 11.5, color: 'var(--muted)', cursor: 'pointer' }}><Icon name="image" size={12} /> Add artwork<input type="file" accept="image/*" onChange={pickImage} style={{ display: 'none' }} /></label>
                      {driveOn && <button type="button" disabled={driveBusy} onClick={fromDrive} style={{ border: '1px dashed var(--hairline)', background: 'none', borderRadius: 7, padding: '4px 10px', fontSize: 11.5, color: 'var(--muted)', cursor: 'pointer' }}>{driveBusy ? 'Opening…' : 'from Drive'}</button>}
                    </>}
                </div>
              )}
              <textarea style={{ ...editIn, resize: 'vertical', lineHeight: 1.45 }} rows={4} value={node.body} placeholder={node.channel === 'sms' ? 'SMS message' : 'Email body'} onChange={(e) => onEdit(node.id, { body: e.target.value })} />
            </>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {!isHtml && <input style={{ ...editIn, maxWidth: 130 }} value={node.ctaText} placeholder="Button label" onChange={(e) => onEdit(node.id, { ctaText: e.target.value })} />}
            <input style={{ ...editIn, flex: 1, minWidth: 120 }} value={node.ctaUrl || ''} placeholder="Link (blank = campaign buy link)" onChange={(e) => onEdit(node.id, { ctaUrl: e.target.value })} />
          </div>
        </div>
      ) : (
        <>
          {node.channel === 'email' && node.subject && <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>{node.subject}</div>}
          {node.channel === 'email' && node.preheader && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 3 }}>{node.preheader}</div>}
          {isWa && <div style={{ fontSize: 12.5, lineHeight: 1.45, whiteSpace: 'pre-wrap', color: 'var(--text)' }}>{waTpl ? (waTpl.body || waTpl.name) : (node.waTemplateId ? 'WhatsApp template' : '(no template picked yet)')}</div>}
          {!isWa && node.body && <div style={{ fontSize: 12.5, lineHeight: 1.45, whiteSpace: 'pre-wrap', color: 'var(--text)' }}>{node.body}</div>}
          {node.ctaText && <div style={{ marginTop: 8 }}><span style={{ display: 'inline-block', fontSize: 11.5, fontWeight: 700, color: 'var(--brand)', border: '1px solid var(--brand)', borderRadius: 8, padding: '3px 9px' }}>{node.ctaText} →</span></div>}
        </>
      )}
      {statChips(node, stats)}
      {onEdit && onGate && <GateBtn onGate={(key) => onGate(node.id, key)} />}
    </div>
  );
}
// ── "Add a check after this step" ────────────────────────────────────────────
// The engine only re-reads someone's signals at a DECISION. Two message steps in
// a row therefore both send, whatever the person did in between — which is how a
// "final reminder, the window closes today" reaches someone who registered
// yesterday. This drops a decision in after a step and moves whatever followed
// onto its "didn't" arm, in place, on a journey that is already running.
function GateBtn({ onGate }) {
  const [open, setOpen] = useState(false);
  const btn = { border: '1px dashed var(--hairline)', background: 'var(--bg)', color: 'var(--muted)', borderRadius: 8, padding: '4px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer', minHeight: 32, textAlign: 'left' };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} style={{ ...btn, marginTop: 8, width: '100%', textAlign: 'center' }}
        title="Put a decision after this step — anything that currently follows moves onto its “didn’t” arm, so the next email only goes to people who still haven’t acted">
        ◆ Add a check after this step
      </button>
    );
  }
  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--muted)' }}>Only carry on if…</span>
      {Object.values(GATES).map((g) => (
        <button key={g.key} type="button" style={btn} onClick={() => { setOpen(false); onGate(g.key); }}>◆ {g.title}</button>
      ))}
      <button type="button" onClick={() => setOpen(false)} style={{ ...btn, border: 'none', textAlign: 'center' }}>Cancel</button>
    </div>
  );
}
// The immediate sync node on a branch (if any) — drives the branch-label ◎ badge
// so it's obvious at a glance that a branch feeds a Meta/TikTok audience.
function branchSync(branch) { return (branch.nodes || []).find((n) => n.type === 'sync') || null; }
function platLabel(p) { return p === 'both' ? 'Meta + TikTok' : p === 'tiktok' ? 'TikTok' : 'Meta'; }
// A journey SYNC node: adds/removes the people who reach this stage to a Meta/
// TikTok custom audience (auto-created on the platform, kept live). Rendered as
// ─── ➡ Handoff card ──────────────────────────────────────────────────────────
// Where people GO from here: into another journey (enrolled the moment they
// arrive) or onto a named list that tracks this stage. The destination list is
// fetched once per (client, journey) and shared by every handoff card in the
// tree — the server decides which journeys are usable and says why for the rest,
// so the picker can never offer a loop or a finished journey.
const targetCache = new Map();
function useHandoffTargets(entityId, actionId, active) {
  const [targets, setTargets] = useState(() => targetCache.get(`${entityId}|${actionId}`) || null);
  useEffect(() => {
    if (!active || !entityId) return undefined;
    const key = `${entityId}|${actionId}`;
    if (targetCache.has(key)) { setTargets(targetCache.get(key)); return undefined; }
    let live = true;
    api.journeyHandoffTargets(entityId, actionId)
      .then((r) => { const t = r.targets || []; targetCache.set(key, t); if (live) setTargets(t); })
      .catch(() => { if (live) setTargets([]); });
    return () => { live = false; };
  }, [entityId, actionId, active]);
  return targets;
}
function HandoffCard({ node, onEdit, onRemove, entityId = '', actionId = '' }) {
  const toJourney = node.target !== 'segment';
  const targets = useHandoffTargets(entityId, actionId, !!onEdit && toJourney);
  const inp = { border: '1px solid rgba(124,58,237,0.45)', borderRadius: 7, background: 'var(--bg)', color: 'var(--text)', padding: '3px 7px', fontSize: 12, fontFamily: 'inherit', minHeight: 34 };
  const chosen = (targets || []).find((t) => t.id === node.targetActionId);
  return (
    <div style={{ padding: '11px 13px', border: '1px solid rgba(124,58,237,0.45)', borderRadius: 12, background: 'rgba(124,58,237,0.10)', width: '100%', boxSizing: 'border-box', textAlign: 'left' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6d28d9' }}>Send to {toJourney ? 'another journey' : 'a list'}</span>
        {onEdit && <MoveHandle id={node.id} />}
        {onEdit && onRemove && <RemoveBtn onClick={() => onRemove(node.id)} title="Remove this handoff step" />}
      </div>
      {onEdit ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 7 }}>
          <select style={inp} value={node.target || 'journey'} onChange={(e) => onEdit(node.id, { target: e.target.value })} aria-label="Where these people go">
            <option value="journey">Into another journey</option>
            <option value="segment">Onto a list (segment)</option>
          </select>
          {toJourney ? (
            <>
              <select style={inp} value={node.targetActionId || ''} onChange={(e) => onEdit(node.id, { targetActionId: e.target.value })} aria-label="Which journey">
                <option value="">{targets === null ? 'Loading journeys…' : 'Pick a journey…'}</option>
                {(targets || []).map((t) => (
                  <option key={t.id} value={t.id} disabled={!t.usable}>{t.title}{t.live ? '' : ` (${t.status})`}{t.usable ? '' : ' — can’t be used'}</option>
                ))}
              </select>
              {/* WHY an option is greyed out. Without it the picker looks broken;
                  "that one hands back to this one" is the whole answer. */}
              {chosen && !chosen.usable && <div style={{ fontSize: 11, color: 'var(--warn,#b45309)', fontWeight: 600 }}>{chosen.why}</div>}
              {targets !== null && !targets.some((t) => t.usable) && <div style={{ fontSize: 11, color: 'var(--muted)' }}>This client has no other journey that can take people yet — create one first, then point this step at it.</div>}
            </>
          ) : (
            <input style={{ ...inp, fontWeight: 700 }} value={node.segmentName || ''} placeholder="List name (e.g. Non-buyers — BF27)" onChange={(e) => onEdit(node.id, { segmentName: e.target.value })} />
          )}
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
            {toJourney
              ? 'Everyone who reaches this step is enrolled in that journey straight away (its own first delay still applies). Nobody can enter a journey they have already been in.'
              : 'Everyone who reaches this step is kept on that list in Engage → Segments — live, so it grows as more people arrive.'}
            {' '}They carry on down this journey too; put this step last on the branch if it should be the exit.
          </div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, marginTop: 5 }}>{toJourney ? (chosen?.title || (node.targetActionId ? 'Another journey' : 'No destination chosen')) : (node.segmentName || 'No list named')}</div>
          {node.note ? <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4, fontStyle: 'italic' }}>{node.note}</div> : null}
        </>
      )}
    </div>
  );
}
// its own distinct card so a segment wired to a branch is unmistakable.
function SyncCard({ node, onEdit, onRemove }) {
  const add = node.action !== 'remove';
  const inp = { border: '1px solid rgba(14,165,233,0.45)', borderRadius: 7, background: 'var(--bg)', color: 'var(--text)', padding: '3px 7px', fontSize: 12, fontFamily: 'inherit' };
  return (
    <div style={{ padding: '11px 13px', border: '1px solid rgba(14,165,233,0.45)', borderRadius: 12, background: 'rgba(14,165,233,0.10)', width: '100%', boxSizing: 'border-box', textAlign: 'left' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15 }}>◎</span>
        <span style={{ fontSize: 11.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: add ? '#0369a1' : '#b45309' }}>{add ? 'Add to' : 'Remove from'} ad audience</span>
        {onEdit && <MoveHandle id={node.id} />}
        {onEdit && onRemove && <RemoveBtn onClick={() => onRemove(node.id)} title="Remove this ad-audience step" />}
      </div>
      {onEdit ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 7 }}>
          <input style={{ ...inp, fontWeight: 700 }} value={node.audienceName || ''} placeholder="Audience name (e.g. FF27 retargeting)" onChange={(e) => onEdit(node.id, { audienceName: e.target.value })} />
          <div style={{ display: 'flex', gap: 6 }}>
            <select style={{ ...inp, flex: 1 }} value={node.platform || 'meta'} onChange={(e) => onEdit(node.id, { platform: e.target.value })}>
              <option value="meta">Meta</option>
              <option value="tiktok">TikTok</option>
              <option value="both">Meta + TikTok</option>
            </select>
            <select style={{ ...inp, flex: 1 }} value={node.action || 'add'} onChange={(e) => onEdit(node.id, { action: e.target.value })}>
              <option value="add">Add people</option>
              <option value="remove">Remove people</option>
            </select>
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{add ? 'Everyone who reaches this step joins the audience (created on the platform for you).' : 'Everyone who reaches this step is pulled out of the audience.'} Also saved as a segment in Engage → Segments. Syncs once the platform is connected in Settings → Integrations.</div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, marginTop: 5 }}>{node.audienceName || 'Journey audience'}</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
            <span style={{ borderRadius: 980, padding: '1px 8px', background: 'rgba(14,165,233,0.16)', color: '#0369a1', fontWeight: 700 }}>{platLabel(node.platform)}</span>
            <span>{add ? 'synced here as people arrive' : 'pulled out at this stage'}</span>
          </div>
          {node.note ? <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4, fontStyle: 'italic' }}>{node.note}</div> : null}
        </>
      )}
    </div>
  );
}
// A dashed "add a step here" control at the tail of a column (edit mode only).
function AddStep({ onAdd }) {
  const b = useContext(BuilderCtx);
  const btn = { border: '1px dashed var(--hairline)', background: 'var(--bg)', color: 'var(--muted)', borderRadius: 8, padding: '3px 9px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' };
  return (
    <>
      <div className="jt-link" />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button type="button" onClick={() => onAdd('email')} style={btn} title="Add an email step here">＋ Email</button>
        <button type="button" onClick={() => onAdd('sms')} style={btn} title="Add an SMS step here">＋ SMS</button>
        {b?.waOn && <button type="button" onClick={() => onAdd('whatsapp')} style={{ ...btn, borderColor: 'rgba(37,211,102,0.5)', color: '#128c4b' }} title="Add a WhatsApp step here — sends an approved template">＋ WhatsApp</button>}
        <button type="button" onClick={() => onAdd('sync')} style={{ ...btn, borderColor: 'rgba(14,165,233,0.5)', color: '#0369a1' }} title="Add / remove everyone who reaches this point to a Meta or TikTok ad audience">＋ ◎ Ad audience</button>
        {b?.handoffOn && <button type="button" onClick={() => onAdd('handoff')} style={{ ...btn, borderColor: 'rgba(124,58,237,0.5)', color: '#6d28d9' }} title="Hand everyone who reaches this point to another journey, or onto a named list">＋ Send to…</button>}
      </div>
    </>
  );
}
function DecisionSplit({ node, stats, onEdit, onRemove, onBranch, onGate, templates, onAdd, path, entityId = '', actionId = '' }) {
  const waiting = (stats && node.id && stats.atNode && stats.atNode[node.id]) || 0;
  const stuck = (stats && node.id && stats.overdue && stats.overdue[node.id]?.n) || 0;
  return (
    <div className="jt-col">
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 13px', borderRadius: 10, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)', maxWidth: onEdit ? 330 : 280, textAlign: 'left' }}>
        <span style={{ fontSize: 14 }}>◆</span>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{node.question}</span>
        {/* A check moves as a WHOLE — its branches travel with it, which is the
            only way "put the whole opened/didn't-open fork later" is one action. */}
        {onEdit && <MoveHandle id={node.id} />}
        {node.kind !== 'split' && (onEdit
          ? <WaitEditor node={node} onEdit={onEdit} />
          : (node.waitHours ? <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, whiteSpace: 'nowrap' }}>· {fmtWait(node.waitHours)}</span> : null))}
        {waiting ? <span style={{ fontSize: 11, fontWeight: 800, color: '#b45309', whiteSpace: 'nowrap' }}>⏳ {waiting} waiting</span> : null}
        {/* Past the window and STILL here. That is a stall, not a wait — the tick
            routes people the moment the window closes — and it means every step
            under this decision is quietly not going out. Reading as ordinary
            "waiting" is what let one sit two days unnoticed. */}
        {stuck ? <span style={{ fontSize: 11, fontWeight: 800, color: '#b91c1c', background: 'rgba(220,38,38,0.10)', border: '1px solid rgba(220,38,38,0.35)', borderRadius: 8, padding: '1px 7px' }}
          title="Their window has already closed but they are still sitting here, so nothing below this decision is going out. Check the campaign is still live, then the server.">{stuck} past the window</span> : null}
        {/* The wording names a duration the engine never reads — only the wait box
            below it is real. Say so where they disagree, rather than letting the
            question quietly misdescribe the flow by a day.
            And FIX it in one tap. Naming a problem without a lever is how "the
            wording says 70 hours, but this waits 4 days" survived a week on a live
            journey: agreeing costs a retype, and neither half is obviously the
            mistake, so it gets left. Both directions are offered because only the
            author knows which one they meant; the buttons just save the typing. */}
        {(() => {
          const mm = waitMismatch(node);
          if (!mm) return null;
          const fix = { border: '1px solid #b45309', background: 'var(--bg)', color: '#b45309', borderRadius: 8, padding: '4px 10px', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', minHeight: 40, fontFamily: 'inherit' };
          return (
            <span style={{ display: 'block', width: '100%', fontSize: 11, fontWeight: 700, color: '#b45309', background: 'rgba(245,158,11,0.18)', borderRadius: 8, padding: '4px 8px', lineHeight: 1.35 }}
              title="The engine only reads the wait box. The question is a label — nobody waits the time it names.">
              ⚠ The wording says {sayHours(mm.saidHours)}, but this waits {sayHours(mm.actualHours)}{onEdit ? ' — fix whichever is wrong' : ''}.
              {onEdit && (
                <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  <button type="button" style={fix} onClick={() => onEdit(node.id, wordingPatchFromWait(node))}
                    title="Keep the window the engine actually runs, and reword the question to match it">Say {sayHours(mm.actualHours)}</button>
                  <button type="button" style={fix} onClick={() => onEdit(node.id, waitPatchFromWording(node))}
                    title="Believe the wording and move the window to match — this changes what the journey DOES">Wait {sayHours(mm.saidHours)}</button>
                </span>
              )}
            </span>
          );
        })()}
        {/* Editing a SPLIT — the field it forks on and which of the client's real
            values go down each arm. Rendered inside the chip so the rule and the
            branches it produces read as one thing. */}
        {onEdit && node.kind === 'split' && (
          <div style={{ width: '100%' }}>
            <input style={{ border: '1px solid var(--hairline)', background: 'var(--bg)', color: 'var(--text)', borderRadius: 8, padding: '7px 9px', fontSize: 12, fontWeight: 700, width: '100%', boxSizing: 'border-box', minHeight: 36 }}
              value={node.question || ''} maxLength={140} placeholder="What is this split asking?"
              onChange={(e) => onEdit(node.id, { question: e.target.value })} />
            <JourneySplitEditor entityId={entityId} node={node} onEdit={onEdit} onBranch={onBranch} />
          </div>
        )}
      </div>
      <div className="jt-stem" />
      <div className="jt-branches">
        {(node.branches || []).map((b, i) => {
          const color = BRANCH_COLORS[i % BRANCH_COLORS.length];
          const sync = branchSync(b);
          return (
            <div className="jt-branch" key={i}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: '#fff', background: color, borderRadius: 980, padding: '2px 10px', whiteSpace: 'nowrap' }}>
                <span style={{ opacity: 0.85 }}>if</span> {b.label}
                {b.when === 'in_segment' && <span style={{ background: 'rgba(255,255,255,0.22)', borderRadius: 980, padding: '1px 7px', fontSize: 10.5 }}>👥 {b.segmentName || 'segment'}{b.segmentId ? '' : ' ⚠ not linked'}</span>}
                {/* A 'clicked' branch can watch ONE link — say which, or the tree
                    reads as "any click" when it isn't. */}
                {b.when === 'clicked' && b.link && <span title={b.link} style={{ background: 'rgba(255,255,255,0.22)', borderRadius: 980, padding: '1px 7px', fontSize: 10.5, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{String(b.link).replace(/^https?:\/\/(www\.)?/, '').slice(0, 34)}</span>}
                {sync && <span style={{ background: 'rgba(255,255,255,0.22)', borderRadius: 980, padding: '1px 7px', fontSize: 10.5 }} title={`${sync.action === 'remove' ? 'Removes from' : 'Adds to'} ${sync.audienceName || 'audience'} on ${platLabel(sync.platform)}`}>◎ {sync.action === 'remove' ? '−' : '+'}{sync.audienceName || 'audience'}</span>}
                {/* HOLD this arm: people who qualify for it wait at the decision
                    instead of being sent down it — and are not re-routed elsewhere. */}
                {onBranch && (
                  <button type="button" onClick={() => onBranch(node.id, i, { paused: !b.paused })}
                    title={b.paused ? 'Resume this branch — anyone held here carries on from where they stopped' : 'Hold this branch — nobody is sent down it until you resume; they wait where they are'}
                    style={{ border: 'none', background: 'rgba(255,255,255,0.22)', color: '#fff', borderRadius: 980, padding: '1px 8px', fontSize: 10.5, fontWeight: 800, cursor: 'pointer', minHeight: 22 }}>{b.paused ? 'Resume' : 'Hold'}</button>
                )}
              </span>
              {b.paused && <span style={{ fontSize: 10.5, fontWeight: 800, color: '#b45309', background: 'rgba(245,158,11,0.14)', borderRadius: 980, padding: '1px 8px', marginTop: 4 }}>held — nobody moves past here</span>}
              <div className="jt-link" />
              {/* An EMPTY branch is a real answer, not a gap: "they registered, so
                  send them nothing more". Say so, or it reads as unfinished. */}
              {!(b.nodes || []).length && (
                <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--muted)', border: '1px dashed var(--hairline)', borderRadius: 10, padding: '8px 12px', textAlign: 'center', width: 210, boxSizing: 'border-box' }}>
                  Ends the journey here
                  <div style={{ fontWeight: 500, marginTop: 2 }}>Nothing more is sent{b.when === 'bought' ? '; they’re counted as converted' : ''}.</div>
                </div>
              )}
              <NodeColumn nodes={b.nodes || []} stats={stats} onEdit={onEdit} onRemove={onRemove} onBranch={onBranch} onGate={onGate} templates={templates} onAdd={onAdd} entityId={entityId} actionId={actionId} path={[...(path || []), { d: node.id, b: i }]} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
// The ⠿ handle: picks a card up (or puts it back down). Also the drag source on
// a desktop, where dragging is what people will try first.
function MoveHandle({ id }) {
  const b = useContext(BuilderCtx);
  if (!b || !b.onMove) return null;
  const armed = b.armed === id;
  return (
    <button type="button" draggable onDragStart={() => b.setArmed(id)} onDragEnd={() => b.setArmed(null)}
      onClick={() => b.setArmed(armed ? null : id)}
      title={armed ? 'Put it back down' : 'Move this step — then pick where it goes'}
      aria-pressed={armed}
      style={{ border: `1px solid ${armed ? 'var(--brand)' : 'var(--hairline)'}`, background: armed ? 'var(--brand)' : 'var(--bg)', color: armed ? '#fff' : 'var(--muted)', borderRadius: 7, padding: '1px 7px', fontSize: 11.5, fontWeight: 700, cursor: armed ? 'grabbing' : 'grab', flex: 'none' }}>⠿</button>
  );
}

// One gap in a branch. `index` is the slot: 0 = before the first node, length =
// after the last, which is why a column of 3 renders 4 of these.
function DropSlot({ path, index }) {
  const b = useContext(BuilderCtx);
  const [over, setOver] = useState(false);
  const [pick, setPick] = useState(false);
  // Read-only (a report, the Owl's confirm card, the recipe previews) draws no
  // slot at all — NodeColumn keeps its own connector lines, so a preview looks
  // exactly as it did before the builder existed.
  if (!b || (!b.onMove && !b.onInsert)) return null;
  const pos = { path: path || [], index };

  if (b.armed) {
    // A refusal is shown ON the slot that would refuse — the alternative is a
    // drop that silently does nothing, which reads as a broken editor.
    const why = b.refuse ? b.refuse(b.armed, pos) : '';
    const drop = () => { if (!why) { b.onMove(b.armed, pos); b.setArmed(null); } };
    return (
      <div onDragOver={(e) => { if (!why) { e.preventDefault(); setOver(true); } }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); drop(); }}
        style={{ padding: '3px 0', width: '100%', display: 'flex', justifyContent: 'center' }}>
        <button type="button" onClick={drop} disabled={!!why} title={why || 'Move it here'}
          style={{ minHeight: 26, padding: '3px 12px', borderRadius: 980, fontSize: 11, fontWeight: 800, cursor: why ? 'not-allowed' : 'pointer',
            border: `1px dashed ${why ? 'var(--hairline)' : 'var(--brand)'}`, opacity: why ? 0.4 : 1,
            background: over ? 'var(--brand)' : 'transparent', color: over ? '#fff' : why ? 'var(--muted)' : 'var(--brand)' }}>
          {why ? 'can’t go here' : 'move here'}
        </button>
      </div>
    );
  }
  if (!b.onInsert) return <div className="jt-link" />; // move-only: keep the gap looking like a connector
  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div className="jt-link" style={{ height: 9 }} />
      {b.atCap ? <div className="jt-link" style={{ height: 9 }} /> : (
        <>
          <button type="button" onClick={() => setPick((v) => !v)} title="Add a step here"
            aria-label="Add a step here" aria-expanded={pick}
            style={{ border: '1px dashed var(--hairline)', background: 'var(--bg)', color: 'var(--muted)', borderRadius: 980, minWidth: 40, minHeight: 22, fontSize: 12, fontWeight: 800, cursor: 'pointer', lineHeight: 1 }}>＋</button>
          <div className="jt-link" style={{ height: 9 }} />
        </>
      )}
      {pick && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setPick(false)} />
          <div className="modal-in" style={{ position: 'absolute', top: 26, zIndex: 41, background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 10, boxShadow: 'var(--shadow-pop, 0 8px 40px rgba(0,0,0,0.16))', padding: 4, display: 'flex', flexDirection: 'column', minWidth: 148 }}>
            {PALETTE.filter((it) => (it.key !== 'handoff' || b.handoffOn) && (it.key !== 'whatsapp' || b.waOn)).map((it) => {
              // A split adds a rung, so a slot already at the nesting limit can't
              // take one. Greyed out with the reason beats a drop that fails.
              const why = b.fits ? b.fits(pos, it) : '';
              return (
                <button key={it.key} type="button" disabled={!!why} title={why || undefined}
                  onClick={() => { setPick(false); b.onInsert(pos, it.key); }}
                  style={{ border: 'none', background: 'transparent', color: why ? 'var(--muted)' : 'var(--text)', opacity: why ? 0.5 : 1, textAlign: 'left', padding: '8px 10px', borderRadius: 7, fontSize: 12.5, fontWeight: 700, cursor: why ? 'not-allowed' : 'pointer', minHeight: 36, whiteSpace: 'nowrap' }}>{it.label}</button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
function NodeColumn({ nodes, stats, onEdit, onRemove, onBranch, onGate, templates, onAdd, path, entityId = '', actionId = '' }) {
  const b = useContext(BuilderCtx);
  const building = !!(b && (b.onMove || b.onInsert));
  const list = nodes || [];
  return (
    <div className="jt-col">
      {building && <DropSlot path={path || []} index={0} />}
      {list.map((n, i) => (
        <Fragment key={n.id || i}>
          {i > 0 && (building ? <DropSlot path={path || []} index={i} /> : <div className="jt-link" />)}
          {n.type === 'decision' ? <DecisionSplit node={n} stats={stats} onEdit={onEdit} onRemove={onRemove} onBranch={onBranch} onGate={onGate} templates={templates} onAdd={onAdd} path={path} entityId={entityId} actionId={actionId} />
            : n.type === 'sync' ? <div className="jt-card" style={onEdit ? { width: 270 } : undefined}><SyncCard node={n} onEdit={onEdit} onRemove={onRemove} /></div>
              : n.type === 'handoff' ? <div className="jt-card" style={onEdit ? { width: 270 } : undefined}><HandoffCard node={n} onEdit={onEdit} onRemove={onRemove} entityId={entityId} actionId={actionId} /></div>
              : <div className="jt-card" style={onEdit ? { width: 270 } : undefined}><MessageCard node={n} stats={stats} onEdit={onEdit} onRemove={onRemove} onGate={onGate} templates={templates} entityId={entityId} /></div>}
        </Fragment>
      ))}
      {/* An EMPTY container's two ends ARE the same slot — render one, not two. */}
      {building && !!list.length && <DropSlot path={path || []} index={list.length} />}
      {onAdd && <AddStep onAdd={(ch) => onAdd(path || [], ch)} />}
    </div>
  );
}
// stats (optional): { byStep: {step:{opened,clicked}}, atNode: {nodeId:count},
// overdue: {nodeId:{n,since}} — parked past their window, i.e. stalled } —
// overlays live funnel chips on the design (see /api/journeys/:e/:id/stats).
// onEdit (optional): (nodeId, patch) — full per-mailer editing in place (copy,
// artwork, link, apply-a-template). templates: campaign templates for the picker.
// `startAt` (ISO): when the journey begins — its scheduled start, or the moment it
// went live. Given one, every step shows the real date it goes out.
export default function JourneyTree({ nodes, stats, onEdit, onRemove, onBranch, onGate, templates, waTemplates = [], onAdd, onMove, onInsert, moveRefusal: refuse, insertRefusal: fits, atCap = false, entityId = '', actionId = '', startAt = '' }) {
  const [armed, setArmed] = useState(null); // the node currently picked up, by id
  const sched = useMemo(() => {
    const t = Date.parse(startAt || '');
    if (!Number.isFinite(t)) return null;
    return { start: t, byId: new Map(journeyFlow(nodes, t).map((r) => [r.id, r])) };
  }, [startAt, nodes]);
  // ➡ The handoff step is flag-gated per client (engage.journey_handoff). It
  // rides the builder context rather than a prop, because the two places that
  // OFFER it — the tail ＋ row and every drop slot — sit at different depths.
  // An existing handoff still RENDERS with the flag off; the engine is what
  // fails closed, and hiding a step already in a live tree would be a lie.
  const treeFlags = useMyFlags(entityId || null);
  const handoffOn = flagOn(treeFlags, 'engage.journey_handoff');
  const waOn = flagOn(treeFlags, 'engage.whatsapp'); // WhatsApp steps: same gate as the WhatsApp campaign channel
  const builder = useMemo(() => ({ armed, setArmed, onMove, onInsert, refuse, fits, atCap, handoffOn, waOn, waTemplates }), [armed, onMove, onInsert, refuse, fits, atCap, handoffOn, waOn, waTemplates]);
  return (
    <ScheduleCtx.Provider value={sched}>
     <BuilderCtx.Provider value={builder}>
      {/* Picked something up? Say what to do next, and give it a way out — a
          lifted card with no visible escape is a trap on a touch screen. */}
      {armed && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 2px 8px', padding: '7px 11px', borderRadius: 10, background: 'rgba(var(--brand-rgb,255,56,92),0.10)', border: '1px solid var(--brand)', fontSize: 12, fontWeight: 700, color: 'var(--brand)' }}>
          <span>Pick where it goes</span>
          <button type="button" onClick={() => setArmed(null)} style={{ marginLeft: 'auto', border: '1px solid var(--brand)', background: 'transparent', color: 'var(--brand)', borderRadius: 8, padding: '3px 10px', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', minHeight: 26 }}>Cancel</button>
        </div>
      )}
      <div className="jt-scroll"><style>{TREE_CSS}</style><NodeColumn nodes={nodes} stats={stats} onEdit={onEdit} onRemove={onRemove} onBranch={onBranch} onGate={onGate} templates={templates} onAdd={onAdd} entityId={entityId} actionId={actionId} path={[]} /></div>
     </BuilderCtx.Provider>
    </ScheduleCtx.Provider>
  );
}

// A fresh message-node id, minted ABOVE the tree's current max so an added step
// never collides with (or renumbers) an existing one — the server preserves any
// m*/d*/y* id it's handed (see server/journeys.js), so an in-flight person keeps
// a valid node_id when a step is added to a RUNNING journey.
export function nextMsgId(nodes, prefix = 'm') {
  let max = 0;
  const walk = (ns) => { for (const n of ns || []) { const m = /^[mdyh](\d+)$/.exec(n && n.id); if (m) max = Math.max(max, Number(m[1])); if (n && n.type === 'decision') for (const b of n.branches || []) walk(b.nodes); } };
  walk(nodes);
  return `${prefix}${max + 1}`;
}
// Immutably remove the node with `id` anywhere in the tree. Returns null when the
// removal would leave the journey invalid — a decision branch with no steps at
// all, or a tree with no message left (server validation would reject the save,
// and an emptied branch would silently stop catching anyone).
export function removeNode(nodes, id) {
  const strip = (ns) => (ns || []).filter((n) => n.id !== id).map((n) => (n.type === 'decision'
    ? { ...n, branches: n.branches.map((b) => ({ ...b, nodes: strip(b.nodes) })) }
    : n));
  const next = strip(nodes);
  // An emptied branch is FINE — it means "ends the journey here", which is how
  // you say "they registered, send them nothing more". What is not fine is a
  // decision where every arm ends the journey (it decides nothing), or a journey
  // left with no messages at all.
  let messages = 0; let deadDecision = false;
  const check = (ns) => { for (const n of ns || []) { if (n.type === 'message') messages += 1; if (n.type === 'decision') { if (!(n.branches || []).some((b) => (b.nodes || []).length)) deadDecision = true; for (const b of n.branches || []) check(b.nodes); } } };
  check(next);
  return messages > 0 && !deadDecision ? next : null;
}
// Immutably append a node to the container at `path` (a list of {d:decisionId,
// b:branchIndex} hops; [] = the trunk). Used by the editor's ＋ Add step.
export function appendStep(nodes, path, node) {
  if (!path || !path.length) return [...(nodes || []), node];
  const [head, ...rest] = path;
  return (nodes || []).map((n) => (n.id === head.d && n.type === 'decision'
    ? { ...n, branches: n.branches.map((b, i) => (i === head.b ? { ...b, nodes: appendStep(b.nodes, rest, node) } : b)) }
    : n));
}
// Total node count (messages + decisions + syncs) — the editor caps adds at the
// server's MAX_NODES so a new step is never silently dropped on save.
export function countAllNodes(nodes) {
  let c = 0;
  for (const n of nodes || []) { c += 1; if (n.type === 'decision') for (const b of n.branches || []) c += countAllNodes(b.nodes); }
  return c;
}

// Immutably apply a copy patch to one message node (by id) anywhere in the tree.
// Patch a decision BRANCH (by the decision's id + the branch index) — pausing an
// arm of the journey is a property of the branch, not of any one step in it.
export function patchBranch(nodes, decisionId, branchIndex, patch) {
  return (nodes || []).map((n) => {
    if (n.type !== 'decision') return n;
    const branches = n.branches.map((b, i) => (n.id === decisionId && i === branchIndex ? { ...b, ...patch } : { ...b, nodes: patchBranch(b.nodes, decisionId, branchIndex, patch) }));
    return { ...n, branches };
  });
}
export function patchNode(nodes, id, patch) {
  return (nodes || []).map((n) => {
    if (n.id === id) return { ...n, ...patch };
    if (n.type === 'decision') return { ...n, branches: n.branches.map((b) => ({ ...b, nodes: patchNode(b.nodes, id, patch) })) };
    return n;
  });
}
// EVERY message in the tree, depth-first, each labelled with its branch path —
// drives "preview all" so branch emails preview too, not just the opening trunk.
export function flattenMessages(nodes, path = []) {
  const out = [];
  for (const n of nodes || []) {
    if (n.type === 'message') out.push({ ...n, branchPath: path.join(' → ') });
    else for (const b of n.branches || []) out.push(...flattenMessages(b.nodes, [...path, b.label]));
  }
  return out;
}
// Every saved segment the tree's decisions WATCH (in_segment branches) — so the
// editor can show which sources drive which parts of the journey at a glance.
export function watchedSegments(nodes) {
  const out = [];
  const walk = (ns) => {
    for (const n of ns || []) {
      if (n.type !== 'decision') continue;
      for (const b of n.branches || []) {
        if (b.when === 'in_segment') out.push({ segmentName: b.segmentName || '(unnamed)', segmentId: b.segmentId || '', question: n.question, branch: b.label });
        walk(b.nodes);
      }
    }
  };
  walk(nodes);
  return out;
}
// Every Meta/TikTok audience this journey builds (sync nodes) with the branch it
// sits on — so the editor can show, at a glance, which ad audiences a journey
// feeds and from which stage. Parallels watchedSegments.
export function syncedAudiences(nodes) {
  const out = [];
  const walk = (ns, branch) => {
    for (const n of ns || []) {
      if (n.type === 'sync') out.push({ audienceName: n.audienceName || 'Journey audience', platform: n.platform || 'meta', action: n.action || 'add', branch });
      else if (n.type === 'decision') for (const b of n.branches || []) walk(b.nodes, b.label);
    }
  };
  walk(nodes, '');
  return out;
}
// The opening (pre-decision) messages — mirrored into the classic `steps` so
// previews + the linear fallback stay coherent with tree edits.
export function openingMessages(nodes) {
  const trunk = [];
  for (const n of nodes || []) {
    if (n.type === 'message') trunk.push(n);
    else if (n.type === 'sync' || n.type === 'handoff') continue; // pass-through — never truncates the opening sequence
    else { if (trunk.length) break; const b = (n.branches || [])[0]; return b ? openingMessages(b.nodes) : trunk; }
  }
  return trunk;
}

export function countDecisions(nodes) {
  let c = 0;
  for (const n of nodes || []) if (n.type === 'decision') { c += 1; for (const b of n.branches || []) c += countDecisions(b.nodes); }
  return c;
}

export { journeyFlow };

// The table. Mobile-first: it scrolls inside its own box rather than pushing the
// page sideways, and the branch column collapses to a chip above the subject on
// a narrow screen (a 5-column grid at 380px is unreadable).
export function JourneyFlow({ nodes, compact = false }) {
  const rows = journeyFlow(nodes);
  if (!rows.length) return null;
  const msgs = rows.filter((r) => !r.sync && !r.handoff).length;
  const decisions = countDecisions(nodes);
  const span = journeySpanHours(nodes);
  const days = Math.ceil(span / 24);
  const cell = { padding: '7px 10px', verticalAlign: 'top', borderTop: '1px solid var(--hairline)', fontSize: 12.5, lineHeight: 1.45 };
  const head = { ...cell, borderTop: 'none', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--muted)', whiteSpace: 'nowrap' };
  return (
    <div style={{ marginTop: compact ? 6 : 10 }}>
      {!compact && <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>Flow summary</div>}
      <div style={{ overflowX: 'auto', border: '1px solid var(--hairline)', borderRadius: 10, background: 'var(--card)' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 520 }}>
          <thead><tr><th style={{ ...head, textAlign: 'left' }}>Branch</th><th style={{ ...head, textAlign: 'left' }}>Step</th><th style={{ ...head, textAlign: 'left' }}>When</th><th style={{ ...head, textAlign: 'left' }}>Subject</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i}>
                <td style={{ ...cell, fontWeight: 700, whiteSpace: 'nowrap' }}>{r.branch || 'All'}</td>
                <td style={{ ...cell, whiteSpace: 'nowrap', color: r.sync ? '#b45309' : r.handoff ? '#6d28d9' : 'var(--text)' }}>{r.sync ? '◎ Audience' : r.handoff ? 'Send to…' : r.kind}</td>
                <td style={{ ...cell, whiteSpace: 'nowrap', color: 'var(--muted)' }}>{r.sendAt
                  ? `on ${new Date(r.sendAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                  : (r.trigger ? `${r.trigger}${r.after ? `, then ${fmtDelay(r.after).replace(/^after /, '+')}` : ''}` : fmtDelay(r.after).replace(/^after /, '+'))}</td>
                <td style={cell}>{r.paused && <span style={{ fontSize: 10.5, fontWeight: 800, color: '#b45309', marginRight: 6 }}>on hold</span>}{r.subject}</td>
              </tr>
            ))}
            <tr>
              <td style={{ ...cell, fontWeight: 700 }}>Total</td>
              <td style={{ ...cell, fontWeight: 700, whiteSpace: 'nowrap' }} colSpan={3}>
                {msgs} message{msgs === 1 ? '' : 's'} · {decisions} decision{decisions === 1 ? '' : 's'}{days ? ` · ${days}-day window` : ''}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
// (Creating the draft happens server-side — POST /api/owl/act/draft-journey —
// which also auto-saves a new chat cohort as a reusable segment first.)
